import { getOwner } from "@ember/owner";
import { run, throttle } from "@ember/runloop";
import Service, { service } from "@ember/service";
import ItsATrap from "@discourse/itsatrap";
import KeyboardShortcutsHelp from "discourse/components/modal/keyboard-shortcuts-help";
import { ajax } from "discourse/lib/ajax";
import domUtils from "discourse/lib/dom-utils";
import { INPUT_DELAY } from "discourse/lib/environment";
import discourseLater from "discourse/lib/later";
import { headerOffset } from "discourse/lib/offset-calculator";
import {
  nextTopicUrl,
  previousTopicUrl,
} from "discourse/lib/topic-list-tracker";
import DiscourseURL from "discourse/lib/url";
import Composer from "discourse/models/composer";
import { capabilities } from "discourse/services/capabilities";

/**
 * @typedef {{
 *   anonymous?: boolean,
 *   global?: boolean,
 *   handler?: string | ((event: KeyboardEvent) => void),
 *   postAction?: string,
 *   click?: string,
 *   path?: string,
 * }} ShortcutBinding
 */

let disabledBindings = [];

export function disableDefaultKeyboardShortcuts(bindings) {
  disabledBindings = disabledBindings.concat(bindings);
}

export function clearDisabledDefaultKeyboardBindings() {
  disabledBindings = [];
}

let extraKeyboardShortcutsHelp = {};

function addExtraKeyboardShortcutHelp(help) {
  const category = help.category;
  if (extraKeyboardShortcutsHelp[category]) {
    extraKeyboardShortcutsHelp[category] = extraKeyboardShortcutsHelp[
      category
    ].concat([help]);
  } else {
    extraKeyboardShortcutsHelp[category] = [help];
  }
}

export function clearExtraKeyboardShortcutHelp() {
  extraKeyboardShortcutsHelp = {};
}

export { extraKeyboardShortcutsHelp as extraKeyboardShortcutsHelp };

/**
 * The binding name of the platform's primary modifier key, as used in key
 * combos such as `${PLATFORM_KEY_MODIFIER}+k`. Derived from the same platform
 * test that drives shortcut display, so a binding and its drawn form cannot
 * disagree on which key is primary.
 */
export const PLATFORM_KEY_MODIFIER = capabilities.isApple ? "meta" : "ctrl";

const NESTED_POST_CONTENT_SELECTOR = [
  ":scope > .nested-post__main > .nested-post__article",
  ":scope > .nested-post__main > .nested-post__collapsed-bar",
  ":scope > .nested-post__main > .nested-post__placeholder",
].join(", ");

const NESTED_POST_SELECTOR = [
  ".nested-view .nested-view__op-article",
  ".nested-view .nested-post",
].join(", ");

const SELECTED_POST_SELECTOR = [
  ".topic-post.selected article[data-post-id]",
  ".nested-view__op-article[data-keyboard-selected][data-post-id]",
  ".nested-post[data-keyboard-selected] > .nested-post__main > .nested-post__article[data-post-id]",
  ".nested-post[data-keyboard-selected] > .nested-post__main > .nested-post__collapsed-bar[data-post-number]",
  ".nested-post[data-keyboard-selected] > .nested-post__main > .nested-post__placeholder[data-post-number]",
].join(", ");

const SELECTED_POST_LIKE_BUTTON_SELECTOR = [
  ".topic-post.selected button.toggle-like",
  ".nested-view__op-article[data-keyboard-selected] button.toggle-like",
  ".nested-post[data-keyboard-selected] > .nested-post__main > .nested-post__article button.toggle-like",
].join(", ");

const SELECTED_POST_DATE_SELECTOR = [
  ".topic-post.selected a.post-date",
  ".nested-view__op-article[data-keyboard-selected] a.post-date",
  ".nested-post[data-keyboard-selected] > .nested-post__main > .nested-post__article a.post-date",
].join(", ");

const DEFAULT_BINDINGS = {
  "!": { postAction: "showFlags" },
  "#": { handler: "goToPost", anonymous: true },
  "/": { handler: "toggleSearch", anonymous: true },
  "ctrl+alt+f": { handler: "toggleSearch", anonymous: true, global: true },
  "=": { handler: "toggleHamburgerMenu", anonymous: true },
  "?": { handler: "showHelpModal", anonymous: true },
  ".": { click: ".alert.alert-info.clickable", anonymous: true }, // show incoming/updated topics
  a: { handler: "toggleArchivePM" },
  b: { handler: "toggleBookmark" },
  c: { handler: "createTopic" },
  "shift+c": { handler: "focusComposer" },
  "command+left": { handler: "webviewKeyboardBack", anonymous: true },
  "command+[": { handler: "webviewKeyboardBack", anonymous: true },
  "command+right": { handler: "webviewKeyboardForward", anonymous: true },
  "command+]": { handler: "webviewKeyboardForward", anonymous: true },
  "mod+p": { handler: "printTopic", anonymous: true },
  d: { postAction: "deletePostWithConfirmation" },
  e: { handler: "editPost" },
  end: { handler: "goToLastPost", anonymous: true },
  "command+down": { handler: "goToLastPost", anonymous: true },
  f: { handler: "toggleBookmarkTopic" },
  "g h": { path: "/", anonymous: true },
  "g l": { path: "/latest", anonymous: true },
  "g n": { path: "/new" },
  "g u": { path: "/unread" },
  "g y": { path: "/unseen" },
  "g c": { path: "/categories", anonymous: true },
  "g t": { path: "/top", anonymous: true },
  "g b": { path: "/my/activity/bookmarks" },
  "g p": { path: "/my/activity" },
  "g m": { path: "/my/messages" },
  "g d": { path: "/my/activity/drafts" },
  "g s": { handler: "goToFirstSuggestedTopic", anonymous: true },
  "g j": { handler: "goToNextTopic", anonymous: true },
  "g k": { handler: "goToPreviousTopic", anonymous: true },
  home: { handler: "goToFirstPost", anonymous: true },
  "command+up": { handler: "goToFirstPost", anonymous: true },
  j: { handler: "selectDown", anonymous: true },
  k: { handler: "selectUp", anonymous: true },
  // we use this odd routing here vs a postAction: cause like
  // has an animation so the widget handles that
  // TODO: teach controller how to trigger the widget animation
  l: { click: SELECTED_POST_LIKE_BUTTON_SELECTOR },
  "m m": { handler: "setTrackingToMuted" }, // mark topic as muted
  "m r": { handler: "setTrackingToRegular" }, // mark topic as regular
  "m t": { handler: "setTrackingToTracking" }, // mark topic as tracking
  "m w": { handler: "setTrackingToWatching" }, // mark topic as watching
  "o,enter": {
    click: [
      ".topic-list tr.selected a.title",
      ".latest-topic-list .latest-topic-list-item.selected div.main-link a.title",
      ".top-topic-list .latest-topic-list-item.selected div.main-link a.title",
      ".latest .featured-topic.selected a.title",
      ".search-results .fps-result.selected .search-link",
    ].join(", "),
    anonymous: true,
  }, // open selected topic on latest or categories page
  tab: { handler: "switchFocusCategoriesPage", anonymous: true },
  p: { handler: "showCurrentUser" },
  q: { handler: "quoteReply" },
  r: { postAction: "replyToPost" },
  s: { click: SELECTED_POST_DATE_SELECTOR, anonymous: true }, // share post
  "shift+j": { handler: "nextSection", anonymous: true },
  "shift+k": { handler: "prevSection", anonymous: true },
  "shift+p": { handler: "pinUnpinTopic" },
  "shift+r": { handler: "replyToTopic" },
  "shift+s": {
    click: "#topic-footer-buttons button.share-and-invite",
    anonymous: true,
  }, // share topic
  "shift+l": { handler: "goToUnreadPost" },
  "shift+z shift+z": { handler: "logout" },
  "shift+f11": { handler: "fullscreenComposer", global: true },
  "shift+u": { handler: "deferTopic" },
  "shift+a": { handler: "toggleAdminActions" },
  "shift+b": { handler: "toggleBulkSelect" },
  t: { postAction: "replyAsNewTopic" },
  u: { handler: "goBack", anonymous: true },
  x: { handler: "bulkSelectItem" },
  "shift+d": {
    click:
      "#dismiss-new-bottom, #dismiss-new-top, #dismiss-topics-bottom, #dismiss-topics-top",
  }, // dismiss new or unread
};

const animationDuration = 100;

function preventKeyboardEvent(event) {
  event.preventDefault();
  event.stopPropagation();
}

export default class KeyboardShortcutLib extends Service {
  @service appEvents;
  @service composer;
  @service currentUser;
  @service router;
  @service siteSettings;
  @service modal;

  constructor() {
    super(...arguments);

    this.keyTrapper = new ItsATrap();
    this._stopCallback();

    // Disable the shortcut if private messages are disabled
    if (!this.currentUser?.can_send_private_messages) {
      delete DEFAULT_BINDINGS["g m"];
    }

    if (disabledBindings.length) {
      disabledBindings.forEach((binding) => delete DEFAULT_BINDINGS[binding]);
    }

    this.bindEvents();
  }

  willDestroy() {
    super.willDestroy(...arguments);
    const prototype = Object.getPrototypeOf(this.keyTrapper);
    prototype.stopCallback = this.oldStopCallback;
    this.oldStopCallback = null;

    this.keyTrapper?.destroy();
    this.keyTrapper = null;
  }

  get isPostTextSelected() {
    const topicController = getOwner(this).lookup("controller:topic");
    return !!topicController.quoteState.postId;
  }

  bindEvents() {
    Object.keys(DEFAULT_BINDINGS).forEach((key) => {
      this.bindKey(key);
    });
  }

  isTornDown() {
    return this.keyTrapper == null;
  }

  /** @param {ShortcutBinding | null} [binding] */
  bindKey(key, binding = null) {
    if (this.isTornDown()) {
      return;
    }

    if (!binding) {
      binding = DEFAULT_BINDINGS[key];
    }

    if (!binding.anonymous && !this.currentUser) {
      return;
    }

    if (binding.path) {
      this._bindToPath(binding.path, key);
    } else if (binding.handler) {
      if (binding.global) {
        // global shortcuts will trigger even while focusing on input/textarea
        this._globalBindToFunction(binding.handler, key);
      } else {
        this._bindToFunction(binding.handler, key);
      }
    } else if (binding.postAction) {
      this._bindToSelectedPost(binding.postAction, key);
    } else if (binding.click) {
      this._bindToClick(binding.click, key);
    }
  }

  // for cases when you want to disable global keyboard shortcuts
  // so that you can override them (e.g. inside a modal)
  pause(combinations) {
    if (this.isTornDown()) {
      return;
    }

    if (!combinations) {
      this.keyTrapper.paused = true;
      return;
    }
    combinations.forEach((combo) => this.keyTrapper.unbind(combo));
  }

  // restore global shortcuts that you have paused
  unpause(combinations) {
    if (this.isTornDown()) {
      return;
    }

    if (!combinations) {
      this.keyTrapper.paused = false;
      return;
    }

    combinations.forEach((combo) => this.bindKey(combo));
  }

  /**
   * addShortcut(shortcut, callback, opts)
   *
   * Used to bind a keyboard shortcut, which will fire the provided
   * callback when pressed. Valid options are:
   *
   * - global     - makes the shortcut work anywhere, including when an input is focused
   * - anonymous  - makes the shortcut work even if a user is not logged in
   * - path       - a specific path to limit the shortcut to .e.g /latest
   * - postAction - binds the shortcut to fire the specified post action when a
   *                post is selected
   * - help       - adds the shortcut to the keyboard shortcuts modal. `help` is an object
   *                with key/value pairs
   *                {
   *                  category: String,
   *                  name: String,
   *                  definition: {
   *                    keys1: ["mod", "shift", "d"],   // key spellings, as for bindings
   *                    keys2: ["alt", "&uarr;"],       // optional second group
   *                    shortcutsDelimiter: "or",       // or | slash | space | newline
   *                  }
   *                }
   *
   * - click      - allows to provide a selector on which a click event
   *                will be triggered, eg: { click: ".topic.last .title" }
   * - context    - a CSS selector string or function that scopes the shortcut.
   *                When provided, the shortcut only fires if the context matches
   *                (element exists in DOM / function returns truthy). Otherwise,
   *                the previously-bound handler for this key fires instead.
   **/
  addShortcut(shortcut, callback, opts = {}) {
    // we trim but leave whitespace between characters, as shortcuts
    // like `z z` are valid for ItsATrap
    shortcut = shortcut.trim();

    if (opts.context) {
      const contextTest =
        typeof opts.context === "function"
          ? opts.context
          : () => document.querySelector(opts.context) !== null;

      const dm = this.keyTrapper._directMap;
      const previousCallback =
        dm[shortcut + ":undefined"] ||
        dm[shortcut + ":keypress"] ||
        dm[shortcut + ":keydown"] ||
        dm[shortcut + ":keyup"];

      const originalCallback = callback;
      callback = function (...args) {
        if (contextTest()) {
          return originalCallback(...args);
        }
        if (previousCallback) {
          return previousCallback(...args);
        }
      };
    }

    this.bindKey(shortcut, { handler: callback, ...opts });
    if (opts.help) {
      addExtraKeyboardShortcutHelp(opts.help);
    }
  }

  /**
   * unbind(combinations)
   *
   * Unbinds all the shortcuts in a key binding object.
   *
   *  - combinations:
   *  {
   *   'c': createTopic
   * }
   */
  unbind(combinations) {
    Object.keys(combinations).forEach((combo) => this.keyTrapper.unbind(combo));
  }

  toggleBookmark(event) {
    const selectedPost = this._getSelectedPost();
    if (selectedPost) {
      preventKeyboardEvent(event);
      this.sendToSelectedPost("toggleBookmark", selectedPost);
      return;
    }

    const selectedTopicListItem = this._getSelectedTopicListItem();
    if (selectedTopicListItem) {
      preventKeyboardEvent(event);
      this.sendToTopicListItemView("toggleBookmark", selectedTopicListItem);
      return;
    }

    this._bookmarkCurrentTopic(event);
  }

  toggleBookmarkTopic(event) {
    const selectedTopicListItem = this._getSelectedTopicListItem();
    if (selectedTopicListItem) {
      preventKeyboardEvent(event);
      this.sendToTopicListItemView("toggleBookmark", selectedTopicListItem);
      return;
    }

    this._bookmarkCurrentTopic(event);
  }

  logout() {
    getOwner(this).lookup("route:application").send("logout");
  }

  quoteReply() {
    if (this.isPostTextSelected) {
      this.appEvents.trigger("quote-button:quote");
      return false;
    }

    this.sendToSelectedPost("replyToPost");
    // lazy but should work for now
    discourseLater(
      () => document.querySelector(".d-editor .quote")?.click(),
      500
    );

    return false;
  }

  editPost() {
    if (this.siteSettings.enable_fast_edit && this.isPostTextSelected) {
      this.appEvents.trigger("quote-button:edit");
      return false;
    } else {
      this.sendToSelectedPost("editPost");
    }

    return false;
  }

  goToNextTopic() {
    nextTopicUrl().then((url) => {
      if (url) {
        DiscourseURL.routeTo(url);
      }
    });
  }

  goToPreviousTopic() {
    previousTopicUrl().then((url) => {
      if (url) {
        DiscourseURL.routeTo(url);
      }
    });
  }

  goToFirstSuggestedTopic() {
    const el = document.querySelector("#suggested-topics a.raw-topic-link");
    if (el) {
      el.click();
    } else {
      const controller = getOwner(this).lookup("controller:topic");
      // Only the last page contains list of suggested topics.
      const url = `/t/${controller.get("model.id")}/last.json`;
      ajax(url).then((result) => {
        if (result.suggested_topics && result.suggested_topics.length > 0) {
          const topic = controller.store.createRecord(
            "topic",
            result.suggested_topics[0]
          );
          DiscourseURL.routeTo(topic.get("url"));
        }
      });
    }
  }

  goToFirstPost() {
    this._jumpTo("jumpTop");
  }

  goToLastPost() {
    this._jumpTo("jumpBottom");
  }

  goToUnreadPost() {
    this._jumpTo("jumpUnread");
  }

  replyToTopic() {
    this._replyToPost();

    return false;
  }

  selectDown() {
    if (document.querySelector(".nested-view")) {
      this._moveAmongNestedPosts(1);
      return;
    }
    this._moveSelection({ direction: 1, scrollWithinPosts: true });
  }

  selectUp() {
    if (document.querySelector(".nested-view")) {
      this._moveAmongNestedPosts(-1);
      return;
    }
    this._moveSelection({ direction: -1, scrollWithinPosts: true });
  }

  bulkSelectItem() {
    const elem = document.querySelector(
      ".selected input.bulk-select, .selected .select-post"
    );
    elem?.click();
  }

  goBack() {
    history.back();
  }

  nextSection() {
    this._changeSection(1);
  }

  prevSection() {
    this._changeSection(-1);
  }

  printTopic(event) {
    run(() => {
      if (document.querySelector(".container.posts")) {
        event.preventDefault(); // We need to stop printing the current page in Firefox
        getOwner(this).lookup("controller:topic").print();
      }
    });
  }

  createTopic(event) {
    if (!(this.currentUser && this.currentUser.can_create_topic)) {
      return;
    }

    event.preventDefault();

    // create new message: if at PM topic route
    if (this.router.currentRouteName.includes("topic")) {
      const topicController = getOwner(this).lookup("controller:topic");
      if (topicController.model.archetype === "private_message") {
        this.composer.open({
          action: Composer.PRIVATE_MESSAGE,
          archetypeId: "private_message",
          draftKey: this.composer.privateMessageDraftKey,
          draftSequence: 0,
        });
        return;
      }
    }

    // create new message: if at messages list route
    if (
      this.router.currentRouteName.includes("userPrivateMessages") ||
      this.router.currentRouteName.includes("group.messages")
    ) {
      const newPMButton = document.querySelector("#new-private-message-btn");
      if (newPMButton) {
        newPMButton.click();
        return;
      }
    }

    // create new topic: context-aware (preserves category, etc.)
    const createTopicButton = document.querySelector("#create-topic");
    if (createTopicButton) {
      createTopicButton.click();
      return;
    }

    // create new topic: default action
    this.composer.open({
      action: Composer.CREATE_TOPIC,
      draftKey: Composer.NEW_TOPIC_KEY,
    });
  }

  focusComposer(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.composer.focusComposer(event);
  }

  fullscreenComposer() {
    if (this.composer.get("model")) {
      this.composer.toggleFullscreen();
    }
  }

  pinUnpinTopic() {
    getOwner(this).lookup("controller:topic").togglePinnedState();
  }

  goToPost(event) {
    preventKeyboardEvent(event);
    this.appEvents.trigger("topic:keyboard-trigger", { type: "jump" });
  }

  toggleSearch(event) {
    this.appEvents.trigger("header:keyboard-trigger", {
      type: "search",
      event,
    });

    return false;
  }

  toggleHamburgerMenu(event) {
    this.appEvents.trigger("header:keyboard-trigger", {
      type: "hamburger",
      event,
    });
  }

  showCurrentUser(event) {
    this.appEvents.trigger("header:keyboard-trigger", { type: "user", event });
  }

  showHelpModal(event) {
    event.preventDefault();
    this.modal.show(KeyboardShortcutsHelp);
  }

  setTrackingToMuted() {
    throttle(this, "_setTracking", 0, INPUT_DELAY, true);
  }

  setTrackingToRegular() {
    throttle(this, "_setTracking", 1, INPUT_DELAY, true);
  }

  setTrackingToTracking() {
    throttle(this, "_setTracking", 2, INPUT_DELAY, true);
  }

  setTrackingToWatching() {
    throttle(this, "_setTracking", 3, INPUT_DELAY, true);
  }

  sendToTopicListItemView(action, elem) {
    elem = elem || document.querySelector("tr.selected.topic-list-item");
    if (elem) {
      const registry = getOwner(this).lookup("-view-registry:main");
      if (registry) {
        const view = registry[elem.id];
        view.send(action);
      }
    }
  }

  currentTopic() {
    const topicController = getOwner(this).lookup("controller:topic");
    if (topicController) {
      const topic = topicController.get("model");
      if (topic) {
        return topic;
      }
    }
  }

  sendToSelectedPost(action, elem) {
    // TODO: We should keep track of the post without a CSS class
    const selectedPost = elem || document.querySelector(SELECTED_POST_SELECTOR);

    let selectedPostId;
    let selectedPostNumber;
    if (selectedPost) {
      selectedPostId = parseInt(selectedPost.dataset.postId, 10);
      selectedPostNumber = parseInt(selectedPost.dataset.postNumber, 10);
    }

    if (selectedPostId || selectedPostNumber) {
      const topicController = getOwner(this).lookup("controller:topic");
      const post = topicController.get("model.postStream.posts").find((p) => {
        if (selectedPostId) {
          return p.id === selectedPostId;
        }

        return p.post_number === selectedPostNumber;
      });
      if (post) {
        // TODO: Use ember closure actions

        let actionMethod = topicController.actions[action];
        if (!actionMethod) {
          const topicRoute = getOwner(this).lookup("route:topic");
          actionMethod = topicRoute.actions[action];
        }

        actionMethod.call(topicController, post);
      }
    }

    return false;
  }

  /** @returns {NodeListOf<Element> | Element[]} */
  categoriesTopicsList() {
    switch (this.siteSettings.desktop_category_page_style) {
      case "categories_with_featured_topics":
        return document.querySelectorAll(".latest .featured-topic");
      case "categories_and_latest_topics":
      case "categories_and_latest_topics_created_date":
        return document.querySelectorAll(
          ".latest-topic-list .latest-topic-list-item"
        );
      case "categories_and_top_topics":
        return document.querySelectorAll(
          ".top-topic-list .latest-topic-list-item"
        );
      default:
        return [];
    }
  }

  deferTopic() {
    getOwner(this).lookup("controller:topic").send("deferTopic");
  }

  toggleAdminActions() {
    document.querySelector(".toggle-admin-menu")?.click();
  }

  toggleBulkSelect() {
    const bulkSelect = document.querySelector("button.bulk-select");

    if (bulkSelect) {
      bulkSelect.click();
    } else {
      getOwner(this).lookup("controller:topic").send("toggleMultiSelect");
    }
  }

  toggleArchivePM() {
    getOwner(this).lookup("controller:topic").send("toggleArchiveMessage");
  }

  webviewKeyboardBack() {
    if (capabilities.isAppWebview) {
      window.history.back();
    }
  }

  webviewKeyboardForward() {
    if (capabilities.isAppWebview) {
      window.history.forward();
    }
  }

  _bookmarkCurrentTopic(event) {
    const topic = this.currentTopic();
    if (topic && document.querySelectorAll(".posts-wrapper").length) {
      preventKeyboardEvent(event);
      getOwner(this).lookup("controller:topic").send("toggleBookmark");
    }
  }

  _jumpTo(direction) {
    if (document.querySelector(".container.posts")) {
      getOwner(this).lookup("controller:topic").send(direction);
    }
  }

  _moveAmongNestedPosts(direction) {
    // Mirrors _moveSelection; nested post wrappers extend through their whole
    // subtrees, so use each wrapper's own content element for height/offset.
    const now = +new Date();
    const fast =
      this._lastMoveTime && now - this._lastMoveTime < 1.5 * animationDuration;
    this._lastMoveTime = now;

    const posts = Array.from(document.querySelectorAll(NESTED_POST_SELECTOR));
    if (!posts.length) {
      return;
    }
    const contentOf = (post) =>
      post.querySelector(NESTED_POST_CONTENT_SELECTOR) || post;

    let selected = posts.find((p) => p.hasAttribute("data-keyboard-selected"));

    if (selected && !fast) {
      const rect = contentOf(selected).getBoundingClientRect();
      if (rect.bottom < headerOffset() || rect.top > window.innerHeight) {
        selected = null;
      }
    }

    if (!selected) {
      const offset = headerOffset();
      selected =
        posts.find((p) => {
          const rect = contentOf(p).getBoundingClientRect();
          return direction > 0 ? rect.top >= offset : rect.bottom >= offset;
        }) || posts[posts.length - 1];
      direction = 0;
    }

    if (!fast && direction !== 0) {
      const selectedContent = contentOf(selected);
      const beginContent = domUtils.offset(selectedContent).top;
      const endContent = beginContent + selectedContent.offsetHeight;
      const beginScreen = window.scrollY;
      const endScreen = beginScreen + window.innerHeight;

      if (direction < 0 && beginScreen > beginContent) {
        return this._scrollTo(
          Math.max(
            beginScreen - window.innerHeight + 3 * headerOffset(),
            beginContent - headerOffset()
          )
        );
      } else if (direction > 0 && endScreen < endContent - headerOffset()) {
        return this._scrollTo(
          Math.min(
            endScreen - 3 * headerOffset(),
            endContent - window.innerHeight
          )
        );
      }
    }

    let next;
    let newIndex = posts.indexOf(selected);
    while (true) {
      newIndex += direction;
      next = posts[newIndex];
      if (!next) {
        return;
      }
      if (contentOf(next).getBoundingClientRect().height > 0) {
        break;
      }
      if (direction === 0) {
        break;
      }
    }

    // Data attribute, not a class — Ember rebuilds .nested-post's class on cloaking/highlight changes and would wipe it.
    for (const p of posts) {
      p.removeAttribute("data-keyboard-selected");
      p.removeAttribute("tabindex");
    }
    next.setAttribute("data-keyboard-selected", "true");
    next.setAttribute("tabindex", "0");
    next.focus({ preventScroll: true });

    // Subscribed by Nested to trigger boundary load-more on last-post selection.
    this.appEvents.trigger("keyboard:move-selection", {
      articles: posts,
      selectedArticle: next,
    });

    const nextContent = contentOf(next);
    const contentTop = domUtils.offset(nextContent).top;
    const contentTopPosition = contentTop - headerOffset();

    // k onto a tall post lands at its bottom page so successive k's scroll up through it.
    if (
      !fast &&
      direction < 0 &&
      nextContent.offsetHeight > window.innerHeight
    ) {
      return this._scrollTo(
        contentTop + nextContent.offsetHeight - window.innerHeight
      );
    }

    this._scrollTo(contentTopPosition);
  }

  _setTracking(levelId) {
    const topic = this.currentTopic();

    if (!topic) {
      return;
    }

    topic.details.updateNotifications(levelId);
  }

  _bindToSelectedPost(action, binding) {
    this.keyTrapper.bind(binding, () => this.sendToSelectedPost(action));
  }

  _bindToPath(path, key) {
    this.keyTrapper.bind(key, () => DiscourseURL.routeTo(path));
  }

  _bindToClick(selector, binding) {
    binding = binding.split(",");
    this.keyTrapper.bind(binding, function (e) {
      const selection = document.querySelector(selector);

      // Special case: We're binding to enter.
      if (e && e.key === "Enter") {
        // Binding to enter should only be effective when there is something
        // to select.
        if (!selection) {
          return;
        }

        // If effective, prevent default.
        e.preventDefault();
      }

      selection?.click();
    });
  }

  _globalBindToFunction(func, binding) {
    let funcToBind = typeof func === "function" ? func : this[func];
    if (typeof funcToBind === "function") {
      this.keyTrapper.bindGlobal(binding, funcToBind.bind(this));
    }
  }

  _bindToFunction(func, binding) {
    let funcToBind = typeof func === "function" ? func : this[func];
    if (typeof funcToBind === "function") {
      this.keyTrapper.bind(binding, funcToBind.bind(this));
    }
  }

  _moveSelection({ direction, scrollWithinPosts }) {
    // Pressing a move key (J/K) very quick (i.e. keeping J or K pressed) will
    // move fast by disabling smooth page scrolling.
    const now = +new Date();
    const fast =
      this._lastMoveTime && now - this._lastMoveTime < 1.5 * animationDuration;
    this._lastMoveTime = now;

    let articles = this._findArticles();
    if (articles === undefined) {
      return;
    }
    articles = Array.from(articles);

    let selected = articles.find((element) =>
      element.classList.contains("selected")
    );
    if (!selected) {
      selected = articles.find(
        (element) => element.dataset.isLastViewedTopic === "true"
      );
    }

    // Discard selection if it is not in viewport, so users can combine
    // keyboard shortcuts with mouse scrolling.
    if (selected && !fast) {
      const rect = selected.getBoundingClientRect();
      if (rect.bottom < headerOffset() || rect.top > window.innerHeight) {
        selected = null;
      }
    }

    // If still nothing is selected, select the first post that is
    // visible and cancel move operation.
    if (!selected) {
      const offset = headerOffset();
      selected = articles.find((article) =>
        direction > 0
          ? article.getBoundingClientRect().top >= offset
          : article.getBoundingClientRect().bottom >= offset
      );
      if (!selected) {
        selected = articles[articles.length - 1];
      }
      direction = 0;
    }

    const index = articles.indexOf(selected);
    let article = selected;

    // Try doing a page scroll in the context of current post.
    if (!fast && direction !== 0 && article && scrollWithinPosts) {
      // The beginning of first article is the beginning of the page.
      const beginArticle =
        article.classList.contains("topic-post") &&
        article.querySelector("#post_1")
          ? 0
          : domUtils.offset(article).top;
      const endArticle = domUtils.offset(article).top + article.offsetHeight;

      const beginScreen = window.scrollY;
      const endScreen = beginScreen + window.innerHeight;

      if (direction < 0 && beginScreen > beginArticle) {
        return this._scrollTo(
          Math.max(
            beginScreen - window.innerHeight + 3 * headerOffset(), // page up
            beginArticle - headerOffset() // beginning of article
          )
        );
      } else if (direction > 0 && endScreen < endArticle - headerOffset()) {
        return this._scrollTo(
          Math.min(
            endScreen - 3 * headerOffset(), // page down
            endArticle - window.innerHeight // end of article
          )
        );
      }
    }

    // Try scrolling to post above or below.
    if (!selected) {
      if (direction === -1 && index === 0) {
        return;
      }
      if (direction === 1 && index === articles.length - 1) {
        return;
      }
    }

    let newIndex = index;
    while (true) {
      newIndex += direction;
      article = articles[newIndex];

      // Element doesn't exist
      if (!article) {
        return;
      }

      // Element is visible
      if (article.getBoundingClientRect().height > 0) {
        break;
      }

      // Safeguard against infinite loops
      if (direction === 0) {
        break;
      }
    }

    this.appEvents.trigger("keyboard:move-selection", {
      articles,
      selectedArticle: article,
    });

    for (const a of articles) {
      a.classList.remove("selected");
      a.removeAttribute("tabindex");
    }
    article.classList.add("selected");
    article.setAttribute("tabindex", "0");
    article.focus();

    const articleTop = domUtils.offset(article).top,
      articleTopPosition = articleTop - headerOffset();
    if (
      scrollWithinPosts &&
      !fast &&
      direction < 0 &&
      article.offsetHeight > window.innerHeight
    ) {
      // Scrolling to the last "page" of the previous post if post has multiple
      // "pages" (if its height does not fit in the screen).
      return this._scrollTo(
        articleTop + article.offsetHeight - window.innerHeight
      );
    } else if (article.classList.contains("topic-post")) {
      return this._scrollTo(
        article.querySelector("#post_1") ? 0 : articleTopPosition
      );
    }

    // Otherwise scroll through the topic list.
    if (
      articleTopPosition > window.pageYOffset &&
      articleTop + article.offsetHeight <
        window.pageYOffset + window.innerHeight
    ) {
      return;
    }

    const scrollRatio = direction > 0 ? 0.2 : 0.7;
    this._scrollTo(articleTopPosition - window.innerHeight * scrollRatio);
  }

  _scrollTo(scrollTop) {
    window.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  }

  _findArticles() {
    let categoriesTopicsList;
    if (document.querySelector(".posts-wrapper")) {
      return document.querySelectorAll(
        ".posts-wrapper .topic-post, .posts-wrapper .post-stream--cloaked, .topic-list tbody tr"
      );
    } else if (document.querySelector(".topic-list")) {
      return document.querySelectorAll(".topic-list .topic-list-item");
    } else if (document.querySelector(".search-results")) {
      return document.querySelectorAll(".search-results .fps-result");
    } else if ((categoriesTopicsList = this.categoriesTopicsList())) {
      return categoriesTopicsList;
    }
  }

  _changeSection(direction) {
    if (document.querySelector(".post-stream")) {
      this._moveSelection({ direction, scrollWithinPosts: false });
    } else {
      const sections = Array.from(
        document.querySelectorAll(".nav.nav-pills li")
      );
      const active = document.querySelector(".nav.nav-pills li.active");
      const index = sections.indexOf(active) + direction;

      if (index >= 0 && index < sections.length) {
        sections[index].querySelector("a, button")?.click();
      }
    }
  }

  _stopCallback() {
    const prototype = Object.getPrototypeOf(this.keyTrapper);
    const oldCallback = (this.oldStopCallback = prototype.stopCallback);

    prototype.stopCallback = function (e, element, combo, sequence) {
      if (this.paused) {
        return true;
      }

      if (
        (combo === "ctrl+f" || combo === "command+f") &&
        element.id === "search-term"
      ) {
        return false;
      }

      return oldCallback.call(this, e, element, combo, sequence);
    };
  }

  _replyToPost() {
    getOwner(this).lookup("controller:topic").send("replyToPost");
  }

  _getSelectedPost() {
    return document.querySelector(SELECTED_POST_SELECTOR);
  }

  _getSelectedTopicListItem() {
    return document.querySelector("tr.selected.topic-list-item");
  }
}
